
import { useState, useEffect, useRef } from 'react';

export default function MissionVision() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeValue, setActiveValue] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const coreValues = [
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Innovation',
      desc: 'Pushing creative boundaries with every project we undertake',
      color: '#038C7F',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      desc: 'Honest partnerships built on trust and transparency',
      color: '#F2780C',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Excellence',
      desc: 'Delivering quality that consistently exceeds expectations',
      color: '#038C7F',
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration',
      desc: 'Working together to achieve shared success and growth',
      color: '#F2780C',
    },
  ];

  // Observe section visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto‑rotate core‑value cards when the component is visible
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveValue((prev) => (prev + 1) % coreValues.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isVisible, coreValues.length]);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-20 lg:py-28 bg-white overflow-hidden"
    >
      {/* Background Gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-[#038C7F]/[0.03] to-transparent rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-gradient-to-tl from-[#F2780C]/[0.03] to-transparent rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Origin Story */}
        <div
          className={`max-w-4xl mx-auto text-center mb-14 sm:mb-20 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#038C7F]/10 rounded-full mb-5">
            <i className="ri-seedling-line text-[#038C7F] text-sm"></i>
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-[#038C7F] uppercase">
              Our Origin
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0D0D0D] leading-tight mb-5">
            The Meaning of{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
              Äsili
            </span>
          </h2>
          <div className="relative max-w-3xl mx-auto">
            <i className="ri-double-quotes-l absolute -top-4 -left-2 text-5xl text-[#038C7F]/10 hidden sm:block"></i>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed">
              In Swahili,{' '}
              <strong className="text-[#038C7F] font-bold">
                &ldquo;Äsili&rdquo;
              </strong>{' '}
              means{' '}
              <em className="text-[#0D0D0D] font-semibold">origin</em> or{' '}
              <em className="text-[#0D0D0D] font-semibold">essence</em>. It
              represents the core of who we are — helping brands discover and
              communicate their authentic identity. We believe every brand has a
              unique story, and our mission is to bring that essence to life
              through strategic creativity and integrated advertising solutions
              across East Africa.
            </p>
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div
          className={`grid md:grid-cols-2 gap-4 sm:gap-6 mb-14 sm:mb-20 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Mission */}
          <div className="group relative bg-gradient-to-br from-[#038C7F] to-[#026b61] rounded-2xl p-6 sm:p-8 lg:p-10 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-xl"></div>
            {/* Decorative corner */}
            <div className="absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-white/20 rounded-tr-xl hidden sm:block"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-rocket-2-line text-xl text-white"></i>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider block">
                    What Drives Us
                  </span>
                  <h3 className="text-xl sm:text-2xl font-black text-white">
                    Our Mission
                  </h3>
                </div>
              </div>
              <p className="text-sm sm:text-base text-white/90 leading-relaxed mb-6">
                To empower enterprises, organizations, and governments with
                integrated advertising and communication systems that strengthen
                credibility, expand markets, and enhance long‑term brand equity
                across East Africa.
              </p>
              <div className="flex items-center gap-3">
                {['Strategy', 'Execution', 'Growth'].map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold text-white/70 uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="group relative bg-gradient-to-br from-[#0D0D0D] to-[#1a1a1a] rounded-2xl p-6 sm:p-8 lg:p-10 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#F2780C]/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#038C7F]/5 rounded-full blur-xl"></div>
            <div className="absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-white/10 rounded-tr-xl hidden sm:block"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-[#F2780C]/20 rounded-xl flex items-center justify-center">
                  <i className="ri-eye-line text-xl text-[#F2780C]"></i>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">
                    Where We&apos;re Headed
                  </span>
                  <h3 className="text-xl sm:text-2xl font-black text-white">
                    Our Vision
                  </h3>
                </div>
              </div>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6">
                To become one of Africa&apos;s leading marketing and advertising
                firms, supporting businesses and nations to advertise
                effectively, present credible brands, and communicate powerfully
                across traditional and digital platforms.
              </p>
              <div className="flex items-center gap-3">
                {['Leadership', 'Innovation', 'Impact'].map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold text-white/50 uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div
          className={`transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F2780C]/10 rounded-full mb-4">
              <i className="ri-heart-pulse-line text-[#F2780C] text-sm"></i>
              <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-[#F2780C] uppercase">
                What We Stand For
              </span>
            </div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#0D0D0D]">
              Our Core{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
                Values
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {coreValues.map((value, index) => (
              <div
                key={index}
                onClick={() => setActiveValue(index)}
                className={`group relative p-5 sm:p-6 rounded-xl cursor-pointer transition-all duration-500 ${
                  activeValue === index
                    ? 'bg-[#0D0D0D] shadow-2xl scale-[1.03]'
                    : 'bg-gray-50 hover:bg-white hover:shadow-lg border border-gray-100 hover:border-gray-200'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {activeValue === index && (
                  <div
                    className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: value.color }}
                  ></div>
                )}
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
                    activeValue === index ? 'bg-white/10' : ''
                  }`}
                  style={{
                    backgroundColor:
                      activeValue === index ? undefined : `${value.color}12`,
                  }}
                >
                  <i
                    className={`${value.icon} text-2xl transition-colors duration-300 ${
                      activeValue === index ? 'text-white' : ''
                    }`}
                    style={{
                      color: activeValue === index ? undefined : value.color,
                    }}
                  ></i>
                </div>
                <h4
                  className={`text-sm sm:text-base font-bold mb-1.5 transition-colors ${
                    activeValue === index ? 'text-white' : 'text-[#0D0D0D]'
                  }`}
                >
                  {value.title}
                </h4>
                <p
                  className={`text-xs sm:text-sm leading-relaxed transition-colors ${
                    activeValue === index ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div
          className={`mt-14 sm:mt-20 transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            {/* Left – Image */}
            <div className="relative group">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#038C7F]/10 to-[#F2780C]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20advertising%20agency%20team%20brainstorming%20session%20in%20modern%20African%20office%20space%20with%20mood%20boards%20brand%20guidelines%20on%20walls%20collaborative%20atmosphere%20warm%20golden%20lighting%20professional%20creative%20workspace%20Kampala%20Uganda%20premium%20photography&width=800&height=600&seq=about-mission-img-v3&orientation=landscape"
                  alt="Asili Visuals Team Collaboration"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 via-transparent to-transparent"></div>
                {/* Floating badge */}
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <div className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                    <span className="text-xs font-bold text-white">
                      Since 2020 · Kampala, Uganda
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right – Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#038C7F]/10 rounded-xl flex items-center justify-center">
                  <i className="ri-star-smile-line text-xl text-[#038C7F]"></i>
                </div>
                <span className="text-xs font-bold text-[#038C7F] uppercase tracking-wider">
                  What Sets Us Apart
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-[#0D0D0D] mb-4 leading-tight">
                Brand Communication as a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
                  System
                </span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">
                We don&apos;t just create campaigns — we build integrated
                communication architectures. Every touchpoint, from your logo
                to your billboard, works together as a unified system that
                strengthens trust and drives growth.
              </p>

              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    icon: 'ri-stack-line',
                    title: 'Integrated Architecture',
                    desc: 'Full-spectrum execution',
                    color: '#038C7F',
                  },
                  {
                    icon: 'ri-global-line',
                    title: 'Regional Intelligence',
                    desc: 'Deep market understanding',
                    color: '#F2780C',
                  },
                  {
                    icon: 'ri-award-line',
                    title: 'Dual Mastery',
                    desc: 'Traditional & modern media',
                    color: '#038C7F',
                  },
                  {
                    icon: 'ri-fingerprint-line',
                    title: 'Strategic Positioning',
                    desc: 'Brands understood & chosen',
                    color: '#F2780C',
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="group/card flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg hover:shadow-gray-100 border border-transparent hover:border-gray-100 transition-all duration-300 cursor-default"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover/card:scale-110"
                      style={{ backgroundColor: `${item.color}12` }}
                    >
                      <i
                        className={`${item.icon} text-sm`}
                        style={{ color: item.color }}
                      ></i>
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-[#0D0D0D] mb-0.5">
                        {item.title}
                      </h4>
                      <p className="text-[10px] sm:text-xs text-gray-500">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
